import { invariant } from '@epic-web/invariant';
import {
  CONFIGURATIONS,
  DELETE,
  GET,
  PUT,
  logger,
  request,
  useConstant,
  useEndpointsContext,
  useObservableValue,
  useStaticSubscription,
  wsScanToMap,
  type DataResponse,
  type IConfiguration,
  type SubscriptionResponse,
} from '@talos/kyoko';
import { isBoolean, isNumber, toNumber } from 'lodash-es';
import { createContext, useCallback, useContext, useMemo, type PropsWithChildren } from 'react';
import { Subject, merge, shareReplay } from 'rxjs';

export type ServerConfigSpec = {
  Key: string;
  Type: string;
  Description?: string;
  Namespaces?: Array<`{string}.`>;
};

export interface OrgConfigurationContextProps {
  /**
   * Reads the current config.
   * @param path The `Key` of the config.
   * @param defaultValue Value to default to. Function provides returnType equal to defaultValue.
   */
  getConfig(path: OrgConfigurationKey, defaultValue: string): string;

  getConfig(path: OrgConfigurationKey, defaultValue: number): number;

  getConfig(path: OrgConfigurationKey, defaultValue: boolean): boolean;

  /**
   * If you are storing a JSON stringified array in org config, you can grab that using this function.
   * If nothing is set (nullish or empty string), your provided defaultValue will be returned.
   * If something is set but we fail to parse for whatever reason, undefined is returned.
   */
  getJSONArrayConfig: <T>(key: OrgConfigurationKey, defaultValue?: T[]) => T[] | undefined;

  /** Raw configuration values */
  configurations?: Map<string, IConfiguration>;
  /** Retrieve list of available server Configs for the Organization */
  getServerConfigList: () => Promise<DataResponse<ServerConfigSpec>>;
  enableTrading: () => Promise<SubscriptionResponse<EmptyObject, 'Configuration'>>;
  disableTrading: () => Promise<SubscriptionResponse<EmptyObject, 'Configuration'>>;
  upsert: (configuration: ConfigurationUpsert) => Promise<SubscriptionResponse<IConfiguration, 'Configuration'>>;
  remove: (key: string) => Promise<SubscriptionResponse<IConfiguration, 'Configuration'>>;
  /** Whether or not the provider is loaded and ready to be used. */
  isLoaded: boolean;
}

export const OrgConfigurationContext = createContext<OrgConfigurationContextProps | undefined>(undefined);
OrgConfigurationContext.displayName = 'OrgConfigurationContext';

export function useOrgConfiguration(): OrgConfigurationContextProps {
  const context = useContext(OrgConfigurationContext);
  if (context === undefined) {
    throw new Error('Missing OrgConfiguration.Provider further up in the tree. Did you forget to add it?');
  }
  return context;
}

/**
 * Provides Organization (per Talos Client) wide settings
 */
export const OrgConfigurationProvider = function OrgConfigurationProvider({ children }: PropsWithChildren) {
  const { orgApiEndpoint } = useEndpointsContext();
  const subject = useConstant(new Subject<SubscriptionResponse<IConfiguration, 'Configuration'>>());
  const { data: subscription } = useStaticSubscription<IConfiguration>({
    name: CONFIGURATIONS,
    tag: 'OrgConfigurationProvider',
  });

  const configurations = useObservableValue(
    () =>
      merge(subscription, subject).pipe(
        wsScanToMap({
          getUniqueKey: item => item.Key,
          newMapEachUpdate: true,
        }),
        shareReplay({
          bufferSize: 1,
          refCount: true,
        })
      ),
    [subscription, subject]
  );

  const isLoaded = useMemo(() => configurations !== undefined, [configurations]);

  const getConfig = useCallback<OrgConfigurationContextProps['getConfig']>(
    function <T extends string | number | boolean>(path: string, defaultValue: T): T {
      return getConfigOrDefault<T>(configurations, path, defaultValue);
    },
    [configurations]
  );

  const getJSONArrayConfig = useCallback(
    <T,>(key: OrgConfigurationKey, defaultValue?: T[]) => {
      const stringifiedArray = getConfig(key, '');

      if (stringifiedArray === '') {
        // No value set, return default value (might be undefined)
        return defaultValue;
      }

      try {
        const array: T[] = JSON.parse(stringifiedArray);
        invariant(Array.isArray(array), `${array} is not an array`);
        return array;
      } catch (e) {
        logger.error(new Error(`Failed to parse ${key}`), {
          extra: { input: stringifiedArray },
        });
        // We return undefined here instead of defaultValue. Something other than the default has been set, but cannot be parsed.
        // Returning the defaultValue might be dangerous. We could parameterize this in the future.
        return undefined;
      }
    },
    [getConfig]
  );

  const enableTrading = useCallback(() => {
    return request(PUT, `${orgApiEndpoint}/enable-trading`);
  }, [orgApiEndpoint]);

  const disableTrading = useCallback(() => {
    return request(PUT, `${orgApiEndpoint}/disable-trading`);
  }, [orgApiEndpoint]);

  const getServerConfigList = useCallback(
    () => request<DataResponse<ServerConfigSpec>>(GET, `${orgApiEndpoint}/configurations/specs`),
    [orgApiEndpoint]
  );

  const upsert = useCallback(
    (configuration: ConfigurationUpsert) => {
      return request(PUT, `${orgApiEndpoint}/configurations`, configuration).then(json => {
        subject.next(json);
        return json;
      });
    },
    [orgApiEndpoint, subject]
  );

  const remove = useCallback(
    (key: string) => {
      return request(DELETE, `${orgApiEndpoint}/configurations`, { Key: key } satisfies ConfigurationRemove).then(
        json => {
          subject.next(json);
          return json;
        }
      );
    },
    [orgApiEndpoint, subject]
  );

  const value = useMemo(() => {
    return {
      getConfig,
      getJSONArrayConfig,
      configurations,
      getServerConfigList,
      upsert,
      remove,
      enableTrading,
      disableTrading,
      isLoaded,
    };
  }, [
    getConfig,
    getJSONArrayConfig,
    configurations,
    getServerConfigList,
    upsert,
    remove,
    enableTrading,
    disableTrading,
    isLoaded,
  ]);

  return <OrgConfigurationContext.Provider value={value}>{children}</OrgConfigurationContext.Provider>;
};

// The casting in this function should not be required but I can't figure it out
export function getConfigOrDefault<T extends string | number | boolean>(
  configurations: Map<string, IConfiguration> | undefined,
  path: string,
  defaultValue: T
): T {
  try {
    if (!configurations) {
      return defaultValue;
    }
    if (isNumber(defaultValue)) {
      const out = toNumber(configurations.get(path)?.Value);
      return out === undefined || isNaN(out) ? defaultValue : (out as T);
    }
    if (isBoolean(defaultValue)) {
      const str = String(configurations.get(path)?.Value).toLowerCase();
      if (['1', 't', 'true'].includes(str)) {
        return true as T;
      }
      if (['0', 'f', 'false'].includes(str)) {
        return false as T;
      }
      return defaultValue;
    }
    const out = configurations.get(path)?.Value;
    return out === undefined ? defaultValue : (out as T);
  } catch (e) {
    logger.error(e as Error);
  }
  return defaultValue;
}

export interface OrgConfiguration {
  Key: OrgConfigurationKey;
  Value: string | number | boolean;
}

export interface ConfigurationUpsert {
  Key: string;
  Value: string;
}

export interface ConfigurationRemove {
  Key: string;
}

export enum OrgConfigurationKey {
  // Add additional currencies to dealer customer credit screen limit dropdown. Expected as comma separated list of Currency.Name
  AdditionalCustomerCreditCurrencies = 'ui.AdditionalCustomerCreditCurrencies',
  // If true, allows authorized users to set customer balance in dealer customer balances screen
  AllowSetCustomerBalance = 'ui.AllowSetCustomerBalance',
  // If true, allows manually expiring customer options positions from dealer customer margin and customer positions. Defaults to false.
  AllowCustomerOptionManualExpiry = 'ui.AllowCustomerOptionManualExpiry',
  // Sets the default number of rows scrolled in most websocket blotters. Default is 10_000
  BlotterRowsMax = 'ui.BlotterRowsMax',
  // Sets the UI reference data cache version. Do this to force a refresh of the cache. Default is '1'. Tell UI team if this is ever used.
  CacheVersion = 'ui.CacheVersion',
  // Sets the TimeInForce parameter for the ClickToTrade feature. Default is 'GoodTillCancel'. No validation is done on this value
  ClickToTradeTimeInForce = 'ui.ClickToTradeTimeInForce',
  // Sets the currencies which should be regarded as "common" and thus be surfaced in the trade spot position selector dropdown. Expected as comma separated list of Currency.Name
  CommonTradeSpotPositionCurrencies = 'ui.CommonTradeSpotPositionCurrencies',
  // If true, allows users to "force withdraw" on dealer/balances New Balance Transaction. Defaults to true for Talos users.
  CustomerBalanceShowForceWithdraw = 'ui.CustomerBalanceShowForceWithdraw',
  // If true, requires an additional confirmation modal before submitting balance transactions. Defaults to false.
  RequireAdditionalConfirmOnBalanceTransactionPreview = 'ui.RequireAdditionalConfirmOnBalanceTransactionPreview',
  // Sets the default number of rows to show in the customer configuration blotter. Default is 10_000.
  CustomerConfigurationRowsMax = 'ui.CustomerConfigurationRowsMax',
  // Sets the REST paging size for the customer configurations screen. Default is 500.
  CustomerConfigurationsPageSize = 'ui.CustomerConfigurationsPageSize',
  // Sets the default number of rows to show in the customer symbol groups blotter. Default is 10_000.
  CustomerSymbolGroupsRowsMax = 'ui.CustomerSymbolGroupsRowsMax',
  // Sets the REST paging size for the customer symbol groups screen. Default is 500.
  CustomerSymbolGroupsPageSize = 'ui.CustomerSymbolGroupsPageSize',
  // Sets additional text to be added to the end of the customer pricing calculator's details when the copy button is pressed.
  CustomerPricingCopyDetailsString = 'ui.CustomerPricingCopyDetailsString',
  // Sets the default number of rows to show in the trade settlement blotter. Default is 5_000
  CustomerSettlementTradeRowsMax = 'ui.CustomerSettlementTradeRowsMax',
  // Sets the delay the UI will wait before asking the backend for Customer Deal Summaries. Use this to prevent too many failed lookups if BE is behind. Default is 0 (ms)
  CustomerSummaryDelayMs = 'ui.CustomerSummaryDelayMs',
  // Sets a delay for the UI to wait before freaking out about missing data when cloning a sub account. Default is 5_000 (ms)
  DuplicateSubAccountRelatedDataFailureTimeoutMs = 'ui.DuplicateSubAccountRelatedDataFailureTimeoutMs',
  /** Enables the new Market Selector. If not set, will be defaulting to true in the UI. */
  EnableNewMarketSelector = 'ui.EnableNewMarketSelector',
  /** Enables the new Market Selector specifically on Admin > Trading Aggregations. Defaults to true. */
  UseNewMarketSelectorInTradingAggregations = 'ui.UseNewMarketSelectorInTradingAggregations',
  /** For the new market selector, will opt in to also apply the async MarketSecurityStatus validation step. Defaults to false. */
  EnableNewMarketSelectorMSSValidation = 'ui.EnableNewMarketSelectorMSSValidation',
  /** Disables the restriction on market account selector that prevents more than one market account being selected per market. Applies only when Unified Liquidity is enabled on a Perp or Swap. */
  DisableUnifiedLiquidityDerivativesMarketSelectionRestriction = 'ui.DisableUnifiedLiquidityDerivativesMarketSelectionRestriction',
  /** When true, bypasses Fuse.js in the SymbolSelector and uses fast substring matching instead.
   * Use for environments with very large symbol lists where Fuse indexing causes UI lag. Default is false. */
  DisableSymbolSelectorFuseSearch = 'ui.DisableSymbolSelectorFuseSearch',
  // Enables the ability to archive orders
  EnableArchiveOrders = 'ui.EnableArchiveOrders',
  // Enables the ability to bulk close positions from the positions blotter
  EnableBulkClosePosition = 'ui.EnableBulkClosePosition',
  /**
   * When true, Positions blotter uses legacy "Update Balances" (PUT /positions/market-accounts via UpdatePositionsDialog)
   * instead of "Adjust Market Account Position" (POST /asset-transactions).
   * Remove this flag and the legacy branch once server version 2.54 is fully retired.
   */
  EnableLegacyUpdateBalances = 'ui.EnableLegacyUpdateBalances',
  // Enables the monitoring buying power blotter
  EnableBuyingPowerMonitoringBlotter = 'ui.EnableBuyingPowerMonitoringBlotter',
  // Enables UI reference data caching. Default is true. If you need to set this down please contact the UI team
  EnableCache = 'ui.EnableCache',
  // Enables trading native calendar spreads in the UI. Defaults to false
  EnableCalendarSpreads = 'ui.EnableCalendarSpreads',
  // Enables the Signals feature in the UI. Defaults to false
  EnableSignals = 'ui.EnableSignals',
  // Enables trading CFDs in the UI. Defaults to false
  EnableCFDs = 'ui.EnableCFD',
  /** Set to true to show the Options selector in the RFQ Form */
  EnableRFQFormOptions = 'ui.EnableRFQFormOptions',
  /** Set to true to show the Split RFQs toggle in the RFQ Form. */
  EnableRFQFormSplitRFQs = 'ui.EnableRFQFormSplitRFQs',
  /** Set to true to show the Size Countering toggle for multi-token Compound instruments in the RFQ Form. */
  EnableRFQSizeCounteringMultitokenCompound = 'ui.EnableRFQSizeCounteringMultitokenCompound',
  /** Set to true to show the Size Countering toggle for Spot instruments in the RFQ Form. */
  EnableRFQSizeCounteringSpot = 'ui.EnableRFQSizeCounteringSpot',
  /** Set to true to show the Partial Fills toggle in the RFQ Form. */
  EnableRFQPartialFills = 'ui.EnableRFQPartialFills',
  /** If true, enables sound effects for manual RFQ interactions. Defaults to false. */
  EnableRfqSoundEffects = 'ui.EnableRfqSoundEffects',
  // Enables the ability to set credit limits per customer market account in dealer customer credit screen. Defaults to true.
  EnableCreditLimitsPerMarketAccount = 'ui.EnableCreditLimitsPerMarketAccount',
  // Adds a select all button for currencies in the balances blotter. Default is false
  EnableCurrenciesFilterSelectAll = 'ui.EnableCurrenciesFilterSelectAll',
  // If true, allows users to set customer account restrictions in dealer customer users screen
  EnableCustomerAccountRestrictions = 'ui.EnableCustomerAccountRestrictions',
  // If true, allows users to set customer trading limits per account in dealer customer users screen. Defaults to true.
  EnableCustomerTradingLimitsPerAccount = 'ui.EnableCustomerTradingLimitsPerAccount',
  // If true shows the advanced derivatives positions blotter in principal
  EnableDerivativesPositionsBlotterAdvanced = 'ui.EnableDerivativesPositionsBlotterAdvanced',
  // If true, allows users to see and use the monitoring blotters. Default is true for Talos users
  EnableMonitoringBlotters = 'ui.EnableMonitoringBlotters',
  // Enable daily Analytics reports in the UI. Defaults to false. Talos users always have access.
  EnableDailyAnalytics = 'ui.EnableDailyAnalytics',
  // The trial end date for daily analytics. Defaults to empty string.
  DailyAnalyticsTrialEndDate = 'ui.DailyAnalyticsTrialEndDate',
  // If true, shows the portfolio settlement screen
  EnablePortfolioSettlement = 'ui.EnablePortfolioSettlement',
  // Of true, requests auxiliary data for orders. Required for DDH and archive orders features. Defaults false
  EnableOrderAuxData = 'ui.EnableOrderAuxData',
  // If true, OTC vanilla option builder sends delta strikes in StrikeDelta and enables Delta strike type in UI. Defaults to false.
  EnableOTCDeltaStrikeSupport = 'ui.EnableOTCDeltaStrikeSupport',
  // If true, shows the Repo product type in the Deal Product Builder. Defaults to false.
  EnableRepoProduct = 'ui.EnableRepoProduct',
  /** When true, enables Open Term loans in Product Builder and shows Open Term loan product templates in Manual Deal. Defaults to false. */
  EnableLoanOpenTermSupport = 'ui.EnableLoanOpenTermSupport',
  /** When true, enables Physical settlement for OTC forwards/options in Product Builder and Physical-settlement product templates in Manual Deal. Defaults to false. */
  EnableOTCPhysicalSettlement = 'ui.EnableOTCPhysicalSettlement',
  /** Enables the new margin management related features that were released in 2.51.0 */
  EnableMarginManagementFeatures = 'ui.EnableMarginManagementFeatures',
  // If true, allows users to create asymmetric exposure limits. Defaults to false.
  EnableNetAllPosition = 'ui.EnableNetAllPosition',
  // If true, enables additional non-native OHLC resolutions (10M, 15M, 30M, 1W, 1MO) in the candlestick chart. Defaults to false. Requires backend flag NonNativeOHLCResolutionsEnabled to be enabled.
  EnableNonNativeOHLCResolutions = 'ui.EnableNonNativeOHLCResolutions',
  // Sets the calculation types for the credit blotter. Expected as comma separated list of CalculationType.Name. Defaults to BuyingPower
  CreditBlotterCalculationTypes = 'ui.CreditBlotterCalculationTypes',
  // If true, shows price grid to users. Defaults to true for Talos users
  EnablePriceGrid = 'ui.EnablePriceGrid',
  // If true, enables column dynamics in the price grid, including adding and removing columns. Defaults to true for Talos users.
  EnablePriceGridColumnDynamics = 'ui.EnablePriceGridColumnDynamics',
  // If true, shows the recon mismatches blotter
  EnableReconMismatchesBlotter = 'ui.EnableReconMismatchesBlotter',
  // If true, shows the spot positions blotter
  EnableSpotPositionsBlotter = 'ui.EnableSpotPositionsBlotter',
  /** When false, position grids ignore server-driven NotionalAmount for display. Defaults to true.
   *
   * TODO: remove by UI v8 (PORT-18540).
   */
  EnableServerDrivenNotionalAmount = 'ui.EnableServerDrivenNotionalAmount',
  /**
   * When true, position grids expose the hidden “Position (Legacy)” column. Default on for Talos users in UI; false otherwise.
   *
   * TODO: remove by UI v8 (PORT-18540).
   */
  EnablePositionLegacyColumn = 'ui.EnablePositionLegacyColumn',
  // Enable the Derivatives tab in the Positions Blotter. Defaults to true.
  EnableDerivativesPositionsBlotter = 'ui.EnableDerivativesPositionsBlotter',
  // Enable the Unified tab in the Positions Blotter. Defaults to true.
  EnableUnifiedPositionsBlotter = 'ui.EnableUnifiedPositionsBlotter',
  // If true, shows the Credit blotter in the UI. Defaults to true.
  EnableCreditBlotter = 'ui.EnableCreditBlotter',
  // If true, shows the Transfers blotter in the UI. Defaults to true.
  EnableTransfersBlotter = 'ui.EnableTransfersBlotter',
  /** If true, will allow the user to select "Derivatives" in the Portfolio > Performance page product type selector. */
  EnablePerformancePageDerivatives = 'ui.EnablePerformancePageDerivatives',
  // If true, shows additional blotter for controlling Market Accounts Trading capabilities. Default is true
  EnableTradingControls = 'ui.EnableTradingControls',
  // If true, Default Settings button/modal in Trading Controls is shown. Default is false
  EnableTradingControlsDefaultSettings = 'ui.EnableTradingControlsDefaultSettings',
  // If enabled, Trading Configuration and Trading Controls Admin users can view and manage all market accounts in Trading Controls, bypassing Account Segregation filters. Default is false
  BypassAccountSegregationChecksForTradingControlsSettings = 'ui.BypassAccountSegregationChecksForTradingControlsSettings',
  /** If true, will show the wallet monitorign related functionality in the ui */
  EnableWalletMonitoring = 'ui.EnableWalletMonitoring',
  // Additional currencies to be included when selecting a home currency for the UI. Expected as comma separated list of Currency.Name
  HomeCurrencyAdditionalCurrencies = 'ui.HomeCurrencyAdditionalCurrencies',
  // Sets the max number of rows the order blotter will load before showing warning. Default is 10_000
  OrderRowsMax = 'ui.OrderRowsMax',
  // Sets the amount of time the UI will wait between requests for a "bulk" operation in the org secmaster screen. Default is 100 (ms)
  OrgSecmasterEditDelay = 'ui.OrgSecmasterEditDelay',
  // Sets the amount of time the UI will wait between requests in the csv order upload screen. Default is 400 (ms)
  PlaceOrderThrottleMillis = 'ui.PlaceOrderThrottleMillis',
  // Sets the max number of subscriptions the price grid will allow. Default is 80
  PriceGridMaxActiveSubscriptions = 'ui.PriceGridMaxActiveSubscriptions',
  // If true, allows user to set pricing rules that allow fills with a synthetic cross. Default is false
  PricingRuleAllowedCrossMarkets = 'ui.PricingRuleAllowedCrossMarkets',
  // If set, allows user to set currencies available for synthetic crosses
  PricingRuleAllowedCrossCurrencies = 'ui.PricingRuleAllowedCrossCurrencies',
  // Sets available product types for Underlying Symbol in Customer Security Master
  CustomerSecurityAllowedUnderlyingProductTypes = 'ui.CustomerSecurityAllowedUnderlyingProductTypes',
  // Sets the number of pricing rules loaded per page in the pricing rules screen. Default is 5000
  PricingRulesPageSize = 'ui.PricingRulesPageSize',
  // If true, shows the uptime chart in the advanced analytics screens
  ShowAnalyticsMarketUptimesChart = 'ui.analytics.ShowMarketUptimesChart',
  // If true, shows the pause subscription button in blotters
  ShowBlotterPauseButton = 'ui.ShowBlotterPauseButton',
  // If true, shows the multileg trade filter dropdown in the Trade Blotter and Order Details → Trades tab. Defaults to false.
  EnableMultilegTradeFilter = 'ui.EnableMultilegTradeFilter',
  // If true, allows customer addresses to be selectable in the portfolio addresses screen
  ShowCustomerDepositAddressSelection = 'ui.ShowCustomerDepositAddressSelection',
  // If true, shows the source account column in the customer deposit address screen
  ShowCustomerDepositSourceAccount = 'ui.ShowCustomerDepositSourceAccount',
  // If true, shows the withdrawal addresses tab in the Customers screen. Defaults to false.
  ShowCustomerWithdrawalAddresses = 'ui.ShowCustomerWithdrawalAddresses',
  // If true, shows the customer order acceptance rules screen in dealer
  ShowCustomerOrderAcceptanceRules = 'ui.ShowCustomerOrderAcceptanceRules',
  // If true, shows the customer configuration / pricing tiers / symbol groups blotters in dealer. Defaults to false.
  ShowCustomerTiering = 'ui.ShowCustomerTiering',
  // If true, shows the customer FIX connections screen in dealer. Defaults to true.
  ShowCustomerFIXConnections = 'ui.ShowCustomerFIXConnections',
  // If true, allows dealers to create their own FIX connections. Defaults to false.
  AllowClientFIXConnectionCreation = 'ui.AllowClientFIXConnectionCreation',
  // If true, shows the customer net acquisition blotters in dealer. Defaults to false.
  ShowCustomerNetAcquisition = 'ui.ShowCustomerNetAcquisition',
  // If true, shows the customer pricing rules (beta) screen in dealer. Defaults to true.
  ShowCustomerPricingRulesBeta = 'ui.ShowCustomerPricingRulesBeta',
  // If true, shows the customer pricing rules (legacy) screen in dealer. Defaults to true.
  ShowCustomerPricingRulesLegacy = 'ui.ShowCustomerPricingRulesLegacy',
  // If true, shows Exchanges tab in Addresses
  ShowExchangeAddresses = 'ui.ShowExchangeAddresses',
  // If true, shows the multileg secmaster screen. Will use the Market status of multileg by default but can be forced by setting to true
  ShowMultiLegSecmaster = 'ui.ShowMultiLegSecmaster',
  // If true, shows the warning column on the positions blotter
  ShowPositionsBlotterWarningColumn = 'ui.ShowPositionsBlotterWarningColumn',
  // If true, shows the security master unified liquidity column. Default to false.
  ShowSecurityMasterUnifiedLiquidityColumn = 'ui.ShowSecurityMasterUnifiedLiquidityColumn',
  // If true, shows the security master fill increment columns. Default to false.
  ShowSecurityMasterFillIncrementColumns = 'ui.ShowSecurityMasterFillIncrementColumns',
  // If true, shows the UseUnderlyingSecurityForRFQ column and drawer field. Default to false.
  ShowSecurityMasterUseUnderlyingSecurityForRFQ = 'ui.ShowSecurityMasterUseUnderlyingSecurityForRFQ',
  // If true, enables the "Create Custom Security" workflow button on the Customer Security Master page. Defaults to false (true for Talos users).
  EnableCreateCustomSecurity = 'ui.EnableCreateCustomSecurity',
  // If true, shows the support menu button in the UI header. Defaults to true.
  EnableHeaderSupportMenu = 'ui.ShowSupportMenu',
  // If true, shows the support chat (intercom) button in the UI. Defaults to true.
  ShowSupportChat = 'ui.ShowSupportChat',
  // Sets mode of sub accounts postition blotter. Valid modes are: "Spot", "Derivatives", "DerivativesAdvanced"
  SubAccountPositionsBlotterMode = 'ui.SubAccountPositionsBlotterMode',
  // Sets the allowed layout arrangements which can be selected in the dealer users screen valid arrangements are
  //   "SingleSymbolView", "MarketDataCards". Value should be comma separated list
  SupportedLayoutArrangements = 'ui.SupportedLayoutArrangements',
  // Sets the allowed strategies when using a specific limit price mode. For example if "Iceberg" is included in this list, then
  //   if Security.SupportedPricingModes.ImpliedVolatility is true, users will be able to place iceberg orders with limit
  //   prices specified in implied volatility. Value should be a comma separated list of Strategy.Name
  SupportedStrategiesForPricingModes = 'ui.SupportedStrategiesForPricingModes',
  // Sets the default number of rows to show in the trade blotter. Default is 10_000
  TradeRowsMax = 'ui.TradeRowsMax',
  /** The max amount of orders to load in for the UI's RECENT_ORDERS subscription. Default is 10 000. */
  RecentOrderRowsMax = 'ui.RecentOrderRowsMax',
  // If true it allows users to see the white label specific navigation items in the dealer menu. Defaults to true for Talos users.
  ViewWhitelabelMenuItems = 'ui.ViewWhitelabelMenuItems',
  /** If true allows users to view the Account Ledger Events page */
  EnableAccountLedgerEventsPage = 'ui.EnableAccountLedgerEventsPage',
  /** Enable allocation controls page in the UI settings. Defaults to true for talos users. */
  EnableAllocationControlsPage = 'ui.EnableAllocationControlsPage',
  /** If true, allows the user to create and clone sub accounts from the sub account selector in the order form */
  EnableOrderFormSubAccountCreation = 'ui.EnableOrderFormSubAccountCreation',
  // If true, shows the Advanced Fee Options section in the Sales Order form. Defaults to false.
  EnableSalesOrderAdvancedFeeOptions = 'ui.EnableSalesOrderAdvancedFeeOptions',
  /** If true, sends a StartDate (now − 2 min) on the CustomerQuote and CustomerExecutionReport subscriptions
   *  opened by the Sales RFQ form, preventing full-history replay. Defaults to false. */
  EnableSalesRFQSubscriptionStartDate = 'ui.EnableSalesRFQSubscriptionStartDate',
  /** If true, allows the user to view the sub account positions blotter in hierarchical / tree mode. Defaults to true. */
  EnableHierarchicalSubAccountPositionsBlotter = 'ui.EnableHierarchicalSubAccountPositionsBlotter',
  /** If true, enables the LP Settlement screen in portfolio/LP-Settlement */
  EnableLpSettlement = 'ui.EnableLpSettlement',
  /** If true, shows OTC Settlement even when LP Settlement is enabled */
  EnableOtcSettlementWithLpSettlement = 'ui.EnableOtcSettlementWithLpSettlement',
  /** If true, enables the Portfolio Management System layout mode */
  EnablePortfolioManagementSystem = 'ui.EnablePortfolioManagementSystem',
  /** If true, allows PMS Lite users to view the Portfolio Management Reports page (for legacy Portfolio Performance view) */
  EnablePortfolioManagementReportsForPMSLite = 'ui.EnablePortfolioManagementReportsForPMSLite',
  /** If true, enables the IBOR/ABOR report in the Portfolio Management Reports page */
  EnablePMSIborAborReport = 'ui.EnablePMSIborAborReport',
  // If set to true, the Sales Order and Sales RFQ customers filtering will ignore Customer Configurations. Defaults to false.
  DisableCustomerFilteringByConfigurations = 'ui.DisableCustomerFilteringByConfigurations',
  /** If true, enables the Balances blotter in the Trading screen. Defaults to true. */
  EnableBalancesBlotter = 'ui.EnableBalancesBlotter',
  /**
   * When enabled, restricts Credit and Balances visibility for users without the VIEW_CREDIT_AND_BALANCES permission.
   * When used with Trading Account Segregation, non-admin users also have restricted access to markets. Defaults to false.
   */
  EnableRestrictedTrading = 'ui.EnableRestrictedTrading',
  /** Enables the Delta-1 BPS Pricing feature in the UI. Defaults to false. */
  EnableDelta1BPSPricingMode = 'ui.EnableDelta1BPSPricingMode',
  /**
   * If true, new orders send Duration when Duration mode is used for EndTime.
   * Modify requests still send EndTime for started orders; only not-started modifies (Staged, and WaitingForStartTime when enabled) send Duration.
   * If false (default), only staged orders send Duration.
   */
  /** TODO: Remove this flag once it is turned to true by default after being live with Pretrade clients for some time after 3.0.0 / Gabriel */
  EnableDurationSubmission = 'ui.EnableDurationSubmission',
  /** When true, only RFQs coupled to a Care Order may be submitted; direct order submission and RFQs without a connection to a CO is disabled. */
  EnableOnlyCareOrderRelatedOrderSubmission = 'ui.EnableOnlyCareOrderRelatedOrderSubmission',
  // Non-UI-specific configurations
  AdminCredentialsBlacklist = 'Admin.CredentialsBlacklist',
  AdminCredentialsWhitelist = 'Admin.CredentialsWhitelist',
  AdminUserAdminCanAddRoles = 'admin.UserAdmin.CanAddRoles',
  AdminUserAdminCanEditOwnRoles = 'admin.UserAdmin.CanEditOwnRoles',
  AdminUserAdminViewUserAPIKey = 'admin.UserAdmin.ViewUserAPIKey',
  AdminUserAdminEditUserAPIKey = 'admin.UserAdmin.EditUserAPIKey',
  AdminUserAdminAdminUserAPIKey = 'admin.UserAdmin.AdminUserAPIKey',
  AdminUserAPIKeyCanAddRoles = 'admin.UserAPIKey.CanAddRoles',
  CustomerRequireWithdrawalApproval = 'customer.RequireWithdrawalApproval',
  CustomerRequireDepositApproval = 'customer.RequireDepositApproval',
  /**
   * Mode for auto-proposing transaction details from treasury-account-links. One of None, ProposeAndAccept, ProposeAcceptAndInitiate, InitiateOnly. Default is None.
   * When the mode is set to None, the system should not propose or initiate transfers.
   */
  CustomerTransactionAutoProposalMode = 'customer.TransactionAutoProposalMode',
  // If true, enables the execution decision logs feature on the customer order timeline. Defaults to false.
  CustomerExecHandlingV2 = 'customer.ExecHandlingV2',

  // enable full Account Segregation functionality
  EnableAccountSegregation = 'trading.EnableAccountSegregation',
  // enable subset of Account Segregation functionality specifically for user/account configuration
  EnableAccountSegregationConfiguration = 'ui.EnableAccountSegregationConfiguration',
  // when Account Segregation is enabled, bypass the Read permission check on the MarketAccountProvider (safety measure for initial rollout in 2.50.3)
  EnableAccountSegregationShowAllMarketAccounts = 'ui.EnableAccountSegregationShowAllMarketAccounts',

  /** An array of string market account names defining the accounts the user can publish balance updates to */
  BalancePublishMarketAccountNames = 'portfolio.BalancePublishMarketAccountNames',
  /** An array of string market names defining the markets the user can publish balance updates to. Defaults to ["internal"] */
  BalancePublishMarketNames = 'portfolio.BalancePublishMarketNames',

  // If true, shows Calls/Puts Multilegs in the Multileg screen dropdown. Backend flag.
  EnableOptionTrading = 'oms.OptionTrading.Enabled',
  // If true, shows 'Option' in tablist and flex layout selection. Existing Option tabs will be removed if toggled off.
  EnableOptionsChainView = 'ui.EnableOptionsChainView',
  OMSPriceProtectionDisabled = 'oms.PriceProtection.Disabled',
  TradingDisableAll = 'trading.DisableAll',
  // Allows editing of market credentials
  EnableMarketCredentials = 'trading.EnableMarketCredentials',
  /** Enable PnLTag related columns for Sub Account Position blotters */
  ShowSubAccountPositionPnLTagColumns = 'ui.ShowSubAccountPositionPnLTagColumns',
  /** If true, shows the End of Day Mark columns in the ABOR/IBOR report blotter */
  ShowIborAborReportEndOfDayMarkColumns = 'ui.ShowIborAborReportEndOfDayMarkColumns',
  /** If true, will show the Sub account position blotter columns related to the Position.SettleGroupValues properties */
  EnableSettleGroupValuesColumns = 'ui.EnableSettleGroupValuesColumns',
  // Enable the trade best execution feature
  EnableTradeBestExecution = 'ui.EnableTradeBestExecution',
  /** For the Settlement Report exporting, defines how many records we allow the frontend to paginate when fetching the trades for a settlement over REST */
  SettlementReportMaxTradeRecordsPaginated = 'ui.SettlementReportMaxTradeRecordsPaginated',
  // Disable validation for Equity to Margin Ratio check on orders, Default is 0, meaning it's enabled by default
  DisableEquityMarginRatioLimitCheck = 'oms.DisableEquityMarginRatioLimitCheck',

  // Enable viewing auto hedging controls and status updates.
  EnableAutoHedging = 'ui.EnableAutoHedging',
  // Enable Dynamic Delta Hedging
  EnableDynamicDeltaHedging = 'ui.EnableDynamicDeltaHedging',
  /** Enable stitching orders with hedge position status based on the HedgeRuleID */
  EnableOrderLinkInHedgeRule = 'ui.EnableOrderLinkInHedgeRule',
  /** The home currency of the autohedger. The asset cannot be selected to be this */
  PositionHedgerHomeCurrency = 'ui.PositionHedgerHomeCurrency',

  /** If true, will allow to file a JIRA ticket directly from the UI */
  EnableSupportModal = 'ui.EnableSupportModal',
  /** If true, allows marking an issue as high priority and sending a PagerDuty alert to the CS team */
  EnableSupportModalPagerDuty = 'ui.EnableSupportModalPagerDuty',
  /** Enables PortfolioSubAccountRecon for PMS-Lite usage (PMS full users get this by default) */
  EnablePortfolioSubAccountRecon = 'ui.EnablePortfolioSubAccountRecon',
  // Disable loading of all customer market accounts. Defaults to false.
  DisableCustomerMarketAccounts = 'ui.DisableCustomerMarketAccounts',
  // Filter market accounts by permission action 'All'. Defaults to false.
  FilterMarketAccountsByPermissionActionAll = 'ui.FilterMarketAccountsByPermissionActionAll',
  /** Force Datadog session replay regardless of sample rate */
  ForceSessionReplay = 'ui.ForceSessionReplay',
  /** Ensures counterparty is a required field on the Customer Balances screen (needed for retail scale) */
  RequireCustomerBalancesCounterpartyFilter = 'ui.RequireCustomerBalancesCounterpartyFilter',

  /** If true, will allow viewing the Margin Management pages in dealer */
  ViewDealerMarginPages = 'ui.ViewDealerMarginPages',

  /** By default, non-synthetic symbols are filtered out from the Customer Book and Modify Trade Dialog.
   *  Set this flag to true if all available options should be visible. */
  // @cspell:disable-next-line - 'Sythetic' is a existing key typo
  AllowSyntheticSymbolsOnManualCustomerTrade = 'ui.AllowSytheticSymbolsOnManualCustomerTrade',
  /** By default, the customer user email is not editable after creation.
   *  [DEAL-4939] Set this flag to true to allow editing the email of the existing customer user.
   *  Defaults to true for Talos users. */
  AllowModifyCustomerUserEmail = 'ui.AllowModifyCustomerUserEmail',
  /** Checks that counterparty/marketaccount has balance to create trade, and will render confirm flow if not. */
  CheckCustomerBalanceOnNewCustomerTrade = 'ui.CheckCustomerBalanceOnNewCustomerTrade',
  /** Number of days before a credential expires to show an alert */
  CredentialAgeAlertDays = 'ui.CredentialAgeAlertDays',
  /** Number of days before a credential expires to show a warning */
  CredentialAgeWarningDays = 'ui.CredentialAgeWarningDays',
  /** Number of days to mute credential expires banner */
  CredentialAgeBannerMuteDays = 'ui.muteCredentialsAgeWarningDays',
  /** List of markets to ignore when showing the credentials age warning banner */
  CredentialAgeWarningIgnoreList = 'ui.CredentialAgeWarningIgnoreList',
  /** Enable Security Master required filtering */
  EnableSecurityMasterRequiredFiltering = 'ui.EnableSecurityMasterRequiredFiltering',
  /** Lets the user select the dark/light or auto adjusted theme */
  EnableThemeTypeSelection = 'ui.EnableThemeTypeSelection',

  /** Enable popout window feature */
  EnablePopoutWindows = 'ui.EnablePopoutWindows',
  /** Enable the mobile layout experience. Defaults to false. */
  EnableMobileLayout = 'ui.EnableMobileLayout',
  /** Enable additional trading panels like order book, trade history and trading view chart */
  EnableAdditionalTradingPanels = 'ui.EnableAdditionalTradingPanels',
  /** Enable volume and price ladders */
  EnableLadderPanels = 'ui.EnableLadderPanels',
  /** Enable trading spaces, allows the user to add spaces and switch between them */
  EnableTradingSpaces = 'ui.EnableTradingSpaces',
  /** Enable monitoring spaces, allows the user to add spaces and switch between them */
  EnableMonitoringSpaces = 'ui.EnableMonitoringSpaces',

  // Toggle between BaseCurrency and Contract quantity for non-spot delta1 multileg
  NonSpotDelta1DefaultQty = 'ui.NonSpotDelta1DefaultQty',

  // Defines the list of Assets/Currencies available in Threshold Asset/Currency dropdowns in Trading Limits. Default: BTC, EUR, USD
  TradingLimitsThresholdCurrencies = 'ui.TradingLimitsThresholdCurrencies',

  // Toggling these flags to true will use the legacy component for each Entity Admin screen
  UseLegacyCounterpartyLimits = 'ui.UseLegacyCounterpartyLimits',
  UseLegacyCustomerAggregations = 'ui.UseLegacyCustomerAggregations',
  /** If true, enables counterparty settings per market account (same as enabled for OTC, but this is for LP settlement scenarios that don't use OTC support). Defaults to false. */
  EnableCounterpartyPerMarketAcctSettings = 'ui.enableCounterpartyPerMarketAcctSettings',

  // Restrict the list of assets that are available as Source and Unified Asset in Unified Liquidity Admin
  UnifiedLiquiditySelfServeAssets = 'ui.UnifiedLiquiditySelfServeAssets',
  EnableUnifiedLiquiditySelfServe = 'ui.EnableUnifiedLiquiditySelfServe',

  /** TODO until we have better bulk edit support in the UI, prevent bulk editing of more than X rows.
   * Defaults to 10_000 */
  EntityAdminMaxBulkEditRows = 'ui.EntityAdminMaxBulkEditRows',

  /** Enable In-app Notifications Displays. Defaults to true. */
  EnableNotifications = 'ui.EnableNotifications',
  /** Enable Destinations Admin (Email/Slack/Telegram) under Admin > Notifications. Defaults to true. */
  EnableNotificationsDestinationsAdmin = 'ui.EnableNotificationsDestinationsAdmin',
  /** Maximum number of notifications that can be archived in a single batch operation **/
  DismissAllNotificationsBatchSize = 'ui.DismissAllNotificationsBatchSize',
  /** Enables Trade Predictions component for VWAP orders **/
  EnableTradePredictions = 'ui.EnableTradePredictions',
  /** Enables Pre-Trade analysis functionality on the order form. Defaults to false **/
  EnablePreTrade = 'ui.EnablePreTrade',
  /** The time in milliseconds for how long the order form can wait for ref data requests before proceeding and initializing the order markets without them. */
  OrderFormMarketSelectionAsyncRefDataTimeoutMs = 'ui.OrderFormMarketSelectionAsyncRefDataTimeoutMs',
  /** If true, shows the TransactTime field in the New Balance Transaction form. Defaults to false. */
  EnableTransactTimeInNewBalanceTransaction = 'ui.EnableTransactTimeInNewBalanceTransaction',
  /** If true, shows the New Balance Transaction form with the Initiate method. Default is false */
  EnableNewCustomerBalanceTransactionDrawer = 'ui.EnableNewCustomerBalanceTransactionDrawer',
  /** If true, shows the deprecated admin tools in the sidebar. Default is false */
  ShowDeprecatedAdminTools = 'ui.ShowDeprecatedAdminTools',
  /** If true, shows the order trajectory chart(s) on the order analytics page. Default is false */
  EnableOrderTrajectoryCharts = 'ui.EnableOrderTrajectoryCharts',
  /** If true, shows order trajectory predictions in order trajectory charts. Default is false, requires EnableOrderTrajectoryCharts = true */
  EnableOrderTrajectoryPredictions = 'ui.EnableOrderTrajectoryPredictions',
  /** If true, enables OTC support features in the UI (OTC blotters, OTC deal product builder, etc.), including OTC V2 features (Options for OTC Derivatives, FloatingRateLoans for Loans). Default is false. */
  EnableOTCSupport = 'ui.EnableOTCSupport',
  /** If true, allows Customer Counterparties to have OTC Market Accounts added and be used in ManualDealTradeView. Default is false. */
  EnableOTCCustomerCounterpartySupport = 'ui.EnableOTCCustomerCounterpartySupport',
  /** If true, disable the option to trade in the cross currency for synthetic crosses. */
  DisableCrossCurrencyQty = 'ui.DisableCrossCurrencyQty',
  /** If true, enabled I Would analytics component for supported orders. Default is true **/
  EnableIWouldAnalytics = 'ui.EnableIWouldAnalytics',

  /** If true, filters out OTC Settlement and Exchange Rebalance accounts with treasury links from balances (old behavior we want to remove). Default is true. */
  EnableTreasuryLinkAccountFiltering = 'ui.EnableTreasuryLinkAccountFiltering',

  /** Set to true to disable the error banner in the blotter from showing at all. Defaults to false. */
  DisableBlotterErrorBanner = 'ui.DisableBlotterErrorBanner',

  // -- ETFRFQ RELATED FLAGS --
  /** Shows Negotiated/Systematic pricing mode toggle in RFQ form */
  EnableNegotiatedRFQFlow = 'ui.EnableNegotiatedRFQFlow',
  /** Disables the PricingMode toggle, forcing the user to only be able to do SpreadToFixing RFQs */
  DisableNonSTFNegotiatedRFQ = 'ui.DisableNonSTFNegotiatedRFQ',
  /** Parser to use for the Care Order import. Valid fields are "aladdin" or "unqork". Defaults to "aladdin". */
  CareOrderImportParser = 'ui.CareOrderImportParser',
  /** When set to true, will show the External ID field in the care order form and all other places this value shows up */
  EnableCareOrderExternalIDField = 'ui.EnableCareOrderExternalIDField',
  /** When set to true, will show the External Comments field in the care order form and all other places this value shows up */
  EnableCareOrderExternalComments = 'ui.EnableCareOrderExternalComments',
  /** When true, will show a button that allows to import basket details via csv upload */
  EnableImportBasketDetails = 'ui.EnableImportBasketDetails',
  /** When set to true, will show the API Import button to allow the user to import basket details to Talos from the unqork api by clicking it*/
  EnableImportBasketDetailsUnqork = 'ui.EnableImportBasketDetailsUnqork',
  /** Stringified JSON object mapping basket SecurityDescription values to UnderlyingSecuritySymbol values. Example: {"BITCOIN":"BTC-USD"} */
  BasketImportUnderlyingSecurityMapping = 'ui.BasketImportUnderlyingSecurityMapping',
  /** If set to true, will display a reference price to the maker when in the apply fixing dialog. Defaults to false. */
  ShowMakerApplyFixingDialogReferencePrice = 'ui.ShowMakerApplyFixingDialogReferencePrice',
  /** If set to true, will display index marks by fixing row in the apply fixing dialog. Defaults to false. */
  ShowMakerApplyFixingDialogIndexMarks = 'ui.ShowMakerApplyFixingDialogIndexMarks',
  /** If set to true, enables IndexMark subscriptions to be made. Defaults to false. */
  EnableIndexMarkSubs = 'ui.EnableIndexMarkSubs',
  /** If set to true, will show the Fund product group option in the RFQ Form. */
  EnableRFQFormFundSecurities = 'ui.EnableRFQFormFundSecurities',
  // Backend key for baskets
  BasketDefinitions = 'basket.Definitions',
  // Backend key for baskets
  BasketQuantityDetails = 'basket.QuantityDetails',
  /** A mapping between Unqork "productId" properties and Talos BasketSymbols (funds) */
  UnqorkProductIDMap = 'rfq.UnqorkProductIDMap',
  /** A mapping between Unqork "authorizedParticipant" properties and Talos Counterparty.Name */
  UnqorkAuthorizedParticipantMap = 'rfq.UnqorkAuthorizedParticipantMap',

  /** When true, will show the Fund field in the Care Order Form */
  EnableCareOrderFormFundField = 'ui.EnableCareOrderFormFundField',
  /** Comma-separated list of values for the Care Order Initiating From field dropdown */
  CareOrderInitiatingFromValues = 'ui.CareOrderInitiatingFromValues',
  /** String array of market names to append maker bid/offer columns for in Care Order CSV exports. Eg ["coinbase","kraken"] */
  CareOrderExportMakerList = 'ui.CareOrderExportMakerList',
  /** When true, will allow the taker to set a Settlement Date on Care Orders */
  EnableCareOrderSettleDateField = 'ui.EnableCareOrderSettleDateField',
  /** When true, enables support for getting adjusted settlement dates from the backend. Defaults to false. */
  SupportAdjustedSettlementDate = 'ui.SupportAdjustedSettlementDate',
  /** Example: {"GBTC":{"Sell":2}} - Override the default settle offset for a fund side in the Care Order form. This is a JSON stringified object. */
  DefaultBasketSettleOffsetOverride = 'ui.DefaultBasketSettleOffsetOverride',

  /** Big flag to enable the ETFRFQ flow in general for Takers, guards a bunch of things */
  EnableTakerMarketplaceETFRFQFlow = 'ui.EnableTakerMarketplaceETFRFQFlow',
  /** When true, strips "-" from CareOrderID before sending it as PostTradeClearingID. */
  TruncateCareOrderID = 'ui.TruncateCareOrderID',
  /** Big flag to enable the ETFRFQ flow in general for Makers, guards a bunch of things */
  EnableMakerMarketplaceETFRFQFlow = 'ui.EnableMakerMarketplaceETFRFQFlow',
  /** When enabled, allows the user to (force) cancel orders associated with Care Orders. */
  AllowCancellingCareOrderChildOrder = 'ui.AllowCancellingCareOrderChildOrder',
  /** When enabled, allows the user to (force) cancel Customer Orders that are SpreadToFixing. */
  AllowCancellingS2FCustomerOrder = 'ui.AllowCancellingS2FCustomerOrder',
  /** When false, will remove all options which write (create, update, delete) Care Orders in the UI. Defaults to true. */
  EnableCareOrderWriting = 'ui.EnableCareOrderWriting',
  /** When true, enables the care-order management blotter panel. */
  EnableCareOrderManagement = 'ui.EnableCareOrderManagement',
  /** When true, enables care-order netting actions and WorkingOrder subscriptions in the care-order blotter. */
  EnableCareOrderNetting = 'ui.EnableCareOrderNetting',
  /** When true, will allow Taker users (Trader Admins) to modify fixing details of terminal orders */
  EnableTakerModifyFixing = 'ui.EnableTakerModifyFixing',
  /** When true, will allow Maker users (Trader Admins) to modify fixing details of terminal orders */
  EnableMakerModifyFixing = 'ui.EnableMakerModifyFixing',
  /** When set to true and when applicable, will show a Please Improve button to the user in the Quote panel */
  EnableQuotePleaseImprove = 'ui.EnableQuotePleaseImprove',
  /** Json blob mapping of Symbol to indexes */
  SpreadToFixingSymbolIndexesMap = 'rfq.SpreadToFixingSymbolIndexesMap',
  // Comma-separated list of markets to use (with useMarketDataSnapshot) to prime the fixing price dialog with a default price
  FixingPriceDialogMarkets = 'ui.FixingPriceDialogMarkets',
  /** If true, will show the Available Amount Details box at the bottom of the Manual Pricing Panel. Defaults to true. */
  EnableManualPricingPanelAvailableAmountDetails = 'ui.EnableManualPricingPanelAvailableAmountDetails',
  /** Enable this flag to have the Care Order export be just a flat list of child orders. These rows will be enriched with certain fields from the CO. */
  EnableFlatCareOrderBlotterExport = 'ui.EnableFlatCareOrderBlotterExport',
  /** When enabled, will let the taker user open the Validate Fixing dialog. */
  EnableTakerValidateFixing = 'ui.EnableTakerValidateFixing',
  /** Set to true to allow the taker to copy details from their fund-related RFQs (in units). Lets them copy strings such as "5u Create GBTC NAV+2.1" to their clipboard. */
  EnableTakerCopyQuoteFundTradeDetails = 'ui.EnableTakerCopyQuoteFundTradeDetails',
  /** Stringified JSON key-value mapping of FundDefinition.Symbol to SubAccount.Name. When a Fund in this record is selected in the CO Form, the SubAccount it is mapped to will be defaulted to. */
  FundCareOrderDefaultSubAccounts = 'ui.FundCareOrderDefaultSubAccounts',

  /** Set to true to use the new Apply Fixing dialog which supports multi-token fixings as well as single-token fixings */
  EnableApplyFixingDialogV2 = 'ui.EnableApplyFixingDialogV2',
  /** Set to true to use the new Modify Fixing dialog (V2) for multi-token fixing support */
  EnableModifyFixingDialogV2 = 'ui.EnableModifyFixingDialogV2',
  /** When set to true, will switch from using the old basket definitions and indexing org configs, to reading the equivalent data from ws: FundDefinition and TradableIndexes subs */
  UseFundDefinitionAndTradableIndexesStreams = 'ui.UseFundDefinitionAndTradableIndexesStreams',
  /** When true, shows the Fund field in the RFQ Form (when applicable). Requires ui.UseFundDefinitionAndTradableIndexesStreams to also be true to appear */
  EnableRFQFormFundField = 'ui.EnableRFQFormFundField',
  /** Set to true to have the UI request the /preview-price endpoint when the maker is pricing a multitoken-quote bottom-up. Just a release related flag, will be removed post 2.55.1 BE is rolled out consistently */
  EnableBottomUpPricingPreviews = 'ui.EnableBottomUpPricingPreviews',
  // -- END ETFRFQ FLAGS --

  /** A development blotter guarding the usage of the new RFQ (Quotes) blotter for principal. Defaults to false. Will probably be removed in time. */
  EnableRFQBlotterV2 = 'ui.EnableRFQBlotterV2',
  /** Set to true to have the RFQ v2 blotter open a ws sub to Care Orders, enriching the Quotes shown with Care Orders with applicable, and making available the relevant columns to display CO information */
  EnrichRFQBlotterV2WithCareOrders = 'ui.EnrichRFQBlotterV2WithCareOrders',
  /** Development flag for showing Bid Qty and Offer Qty columns in the Quotes V2 blotter. Defaults to false. */
  EnableQuotesBidOfferQtyColumns = 'ui.EnableQuotesBidOfferQtyColumns',
  /** Temporary dev flag for the Quotes V2 sweep button. Replace with the final product flag before release. */
  ShowQuotesBlotterSweepButtonV0 = 'ui.ShowQuotesBlotterSweepButtonV0',

  /** When disabled, the API Key menu will not be available in the principal UI settings menu. Defaults to true. */
  EnableUserAPIKey = 'ui.EnableUserAPIKey',
  /** Filename matching client identifier in assets/logos (example.svg)*/
  ClientLogoName = 'ui.ClientLogoName',
  /** Custom document title for client branding*/
  ClientTitle = 'ui.ClientTitle',
  /**
   * If true, enables optimisations suited to very large datasets that occur in retail-scale deployments (e.g. 600k+ customers):
   * disables Fuse.js on the Customer and Customer Market Account selectors.
   * Defaults to false.
   */
  RetailScale = 'ui.RetailScale',

  /**
   * Comma-separated list of subscription names to block at the WebSocket level.
   * Use this to temporarily disable problematic subscriptions that are causing issues for a client in case of an emergency.
   * NOTE: IN A MULTI-STREAM SUBSCRIPTION, ALL STREAMS IN THE SUBSCRIPTION WILL BE BLOCKED IF ANY ONE OF THEM IS IN THE LIST.
   * Example: "BalanceDelta,BalanceSnapshot"
   */
  BlockSubscriptions = 'ui.BlockSubscriptions',
  /** If true, enables the HaircutRatio parameter for multileg securities and orders. Defaults to false. */
  EnableHaircutRatio = 'ui.EnableHaircutRatio',
  /** If true, displays a "Network 2" indicator in the Talos Admin header. Defaults to false. */
  EnableNetworkTwo = 'ui.EnableNetworkTwo',

  // -- COINMETRICS --
  /** API key for the CoinMetrics Pro tier. Leave empty for community-only access. */
  CoinMetricsApiKey = 'ui.CoinMetricsApiKey',

  /**
   * Pipe-delimited list of quote currency groups, where each group is a comma-delimited list of
   * currencies treated as interchangeable when validating UnderlyingCrossSymbol selections.
   * Example: "USD,USDT|EUR,EURC" defines two groups: {USD, USDT} and {EUR, EURC}.
   * If absent or empty, falls back to strict quote currency matching (existing behavior).
   */
  UnifiedQuoteCurrencyGroups = 'ui.CustomerSecurityUnifiedQuoteCurrencyGroups',
}
